import { CreatePortfolioData, PortfolioDataResponse, PortfolioStatusState } from 'types/portfolios/portfolioData';
import { brsApi, employeeApi, lookupConfig, portfolioApi, serviceAgreementApi } from '../../api';
import {
  type BeneficialOwnerPartnerRole,
  type GetPortfolioListResponse,
  type LookupEntity,
  type PortfolioDetails,
  type PortfolioTypeRule,
  type SequenceNumbersForBrResponse,
  CashAccountSource,
  type RevisionHistoryResponse,
  type ServiceAgreementV2,
  type Currencies,
  type Employee,
} from 'pco-product-service-internal-consumer';
import axios from 'axios';
import { REACT_APP_API_URL } from 'services/constants';

export enum FeatureFlagName {
  PRIVATE_BANKING_BR_ENABLED = 'PRIVATE_BANKING_BR_ENABLED',
  PUBLIC_PORTFOLIO_SEARCH_BY_BR_API_ENABLED = 'PUBLIC_PORTFOLIO_SEARCH_BY_BR_API_ENABLED',
  PORTFOLIO_BATCH_CASH_ACCOUNT_CREATE_API_ENABLED = 'PORTFOLIO_BATCH_CASH_ACCOUNT_CREATE_API_ENABLED',
  SERVICE_AGREEMENT_ACCESS_CHECK_ENABLED = 'SERVICE_AGREEMENT_ACCESS_CHECK_ENABLED',
  CHECK_MISSING_PARENT_LEVEL_ENABLED = 'CHECK_MISSING_PARENT_LEVEL_ENABLED',
  CREATE_FORMS_IF_NOT_EXIST_ENABLED = 'CREATE_FORMS_IF_NOT_EXIST_ENABLED',
  PAO_ENABLED = 'PAO_ENABLED',
  PORTFOLIO_UPDATE_API_ENABLED = 'PORTFOLIO_UPDATE_API_ENABLED',
  PORTFOLIO_CREATE_API_ENABLED = 'PORTFOLIO_CREATE_API_ENABLED',
  PORTFOLIO_SEARCH_BY_SERVICE_AGREEMENT_API_ENABLED = 'PORTFOLIO_SEARCH_BY_SERVICE_AGREEMENT_API_ENABLED',
  PUBLIC_PORTFOLIO_CREATE_API_ENABLED = 'PUBLIC_PORTFOLIO_CREATE_API_ENABLED',
  PORTFOLIO_KAFKA_PUBLISH_ENABLED = 'PORTFOLIO_KAFKA_PUBLISH_ENABLED',
  VIDEO_IDENTIFICATION_ENABLED = 'VIDEO_IDENTIFICATION_ENABLED',
  MODIFICATION_SERVICE_AGREEMENT_ENABLED = 'MODIFICATION_SERVICE_AGREEMENT_ENABLED',
  PORTFOLIO_BATCH_CASH_ACCOUNT_UPDATE_API_ENABLED = 'PORTFOLIO_BATCH_CASH_ACCOUNT_UPDATE_API_ENABLED',
  PORTFOLIO_SEARCH_BY_BR_API_ENABLED = 'PORTFOLIO_SEARCH_BY_BR_API_ENABLED',
  PUBLIC_PORTFOLIO_GET_BY_ID_API_ENABLED = 'PUBLIC_PORTFOLIO_GET_BY_ID_API_ENABLED',
  PCA_V3_ENABLED = 'PCA_V3_ENABLED',
  SEARCH_ACCOUNT_ENABLED = 'SEARCH_ACCOUNT_ENABLED',
  ROC_ENABLED = 'ROC_ENABLED',
}

export interface FeatureFlag {
  name: FeatureFlagName;
  enabled: boolean;
  value: string | null;
  modifiedBy: string | null;
  modifiedAt: string | null;
  readOnly: boolean;
  contexts: string[];
}

export const createPortfolio = async (params: CreatePortfolioData): Promise<PortfolioDetails> => {
  const cashAccounts = params?.cashAccounts?.map((item) => ({
    ...item,
    blocked: false, //Not part of BROMWAVE2, will update the change in ROC
    accountStatus: PortfolioStatusState.INITIAL,
  }));
  const { portfolioTypeNumber, sequenceNumber, ...rest } = params;
  // The UI composes the dotted portfolio number (TT.SS) from the type number and the zero-padded sequence number
  const first = portfolioTypeNumber || '';
  const portfolioNumber = `${first}.${String(sequenceNumber).padStart(2, '0')}`;
  const response = await portfolioApi.createPortfolio({
    portfolioCreateRequest: {
      ...rest,
      sequenceNumber,
      portfolioStatus: PortfolioStatusState.INITIAL,
      portfolioNumber,
      cashAccounts,
    },
  });
  return response;
};

export const updatePortfolio = async (params: PortfolioDataResponse): Promise<PortfolioDetails> => {
  const cashAccounts = params?.cashAccounts?.map((item) => ({
    ...item,
    blocked: item?.blocked ?? false, //Not part of BROMWAVE2, will update the change in ROC
    accountStatus: item?.accountStatus ?? PortfolioStatusState.INITIAL,
  }));

  const response = await portfolioApi.updatePortfolio({
    portfolioUid: params.portfolioId,
    portfolioUpdateRequest: { ...params, cashAccounts },
  });
  return response;
};

export const getDropdown = async (configType: string): Promise<Array<LookupEntity>> => {
  const response = await lookupConfig.getConfigValues({ config: configType });
  return response;
};

export const getServiceAgreement = async (serviceAgreementId: string): Promise<ServiceAgreementV2> => {
  const response = await serviceAgreementApi.getServiceAgreement({ serviceAgreementUid: serviceAgreementId });
  return response;
};

export const getPortfolios = async (brUid: string, source: CashAccountSource): Promise<GetPortfolioListResponse> => {
  const response = await portfolioApi.getPortfoliosByBusinessRelationshipId({
    brUid,
    cashAccountSource: source,
  });
  return response;
};

export const getPortfolioDetails = async (serviceAgreementId: string): Promise<PortfolioDetails> => {
  const response = await portfolioApi.getPortfolioByServiceAgreementId({ serviceAgreementId });
  return response;
};

export const getBeneficialOwners = async (brUid: string): Promise<Array<BeneficialOwnerPartnerRole>> => {
  const response = await brsApi.getBeneficialOwnersToBr({ brUid });
  return response;
};

export const getPortfolioRules = async (brUid: string): Promise<PortfolioTypeRule[]> => {
  const response = await portfolioApi.getPortfolioRulesForBr({ brUid });
  return response.portfolioTypes;
};

export const getReferenceCurrency = async (brUid: string): Promise<Currencies> => {
  const response = await brsApi.getBrCurrencies({ brUid });
  return response;
};

export const getSequenceNumber = async (brUid: string, accountType: string): Promise<SequenceNumbersForBrResponse> => {
  const response = await portfolioApi.getSequenceNumbersForBr({ brUid, accountType });
  return response;
};

export const addCashAccounts = async (
  portfolioUid: string,
  cashAccounts: {
    currency: string;
    accountType: string;
  }[],
): Promise<PortfolioDetails> => {
  const newCashAccounts = cashAccounts?.map((item) => ({
    currency: item.currency,
    accountType: item.accountType,
    accountStatus: PortfolioStatusState.INITIAL,
  }));
  const response = await portfolioApi.batchCreatePortfolioCashAccounts({
    portfolioUid: portfolioUid,
    portfolioCashAccountBatchRequest: { cashAccounts: newCashAccounts },
  });

  return response;
};

export const getPortfolioHistory = async (portfolioUid: string): Promise<RevisionHistoryResponse> => {
  const response = await portfolioApi.getPortfolioHistory({ portfolioUid });
  return response;
};

export const getEmployees = async (): Promise<Array<Employee>> => {
  const response = await employeeApi.getEmployees();
  return response;
};

//Not using connect as gcrm infra has not included connector yet
export const getFeatureFlags = async (): Promise<Array<FeatureFlag>> => {
  const response = await axios.get(`${REACT_APP_API_URL}/public/v1/feature-flags`);
  return response.data;
};
